
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase/client';
import type { Area } from 'react-easy-crop';
import { pruefePasswort, type ProfilDaten } from '../../../lib/profil/profil';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { useViewport } from '../../../components/island/hooks';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { Hero, Meldung, ProfilTabs, type ProfilTab } from './teile/Kopf';
import { AllgemeinTab } from './teile/AllgemeinTab';
import { SicherheitTab } from './teile/SicherheitTab';
import { BildAnpassenOverlay, LoginHistorieOverlay, type LoginEvent } from './teile/Overlays';

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err));

interface ProfileViewProps {
  userProfile: ProfilDaten | null;
  onRefresh: () => void;
}


const ProfileView: React.FC<ProfileViewProps> = ({ userProfile, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<ProfilTab>('general');
  const { mobil } = useMobilModus();
  const breite = useViewport().width;
  const schmal = breite < 640;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cropper State
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  // History State
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<LoginEvent[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('login_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      setHistory(data || []);
    } catch (err) {
      console.error("History fetch error", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenHistory = () => {
    setShowHistory(true);
    void fetchHistory();
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Bitte wählen Sie ein gültiges Bildformat.');
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageToCrop(reader.result as string);
    });
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) throw new Error('No 2d context');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };

  const handleUploadCroppedImage = async () => {
    if (!imageToCrop || !croppedAreaPixels || !userProfile) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const fileExt = 'jpg';
      const filePath = `public/profile/${userProfile.id}_${Date.now()}.${fileExt}`;

      // 1. Upload
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, croppedBlob, { 
          cacheControl: '3600',
          upsert: true 
        });

      if (uploadError) throw uploadError;

      // 2. URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // 3. Update DB
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userProfile.id);

      if (updateError) throw updateError;

      setSuccess('Profilbild erfolgreich aktualisiert.');
      setImageToCrop(null);
      onRefresh();
    } catch (err: unknown) {
      setError('Fehler beim Verarbeiten des Bildes: ' + fehlerText(err));
    } finally {
      setUploading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    const fehler = pruefePasswort(newPassword, confirmPassword);
    if (fehler) {
      setError(fehler);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      
      setSuccess('Passwort wurde erfolgreich aktualisiert.');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setError((err as { message?: string } | null)?.message || 'Fehler beim Aktualisieren des Passworts.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div className="mx-auto flex w-full flex-col" style={{ maxWidth: 1040, gap: 14 }}>
        <Hero profil={userProfile} schmal={schmal} uploading={uploading} fileInputRef={fileInputRef}
          onAvatarClick={handleAvatarClick} onFileSelect={handleFileSelect} />

        {error && <Meldung art="fehler" text={error} onSchliessen={() => setError(null)} mobil={mobil} />}
        {success && <Meldung art="erfolg" text={success} onSchliessen={() => setSuccess(null)} mobil={mobil} />}

        <ProfilTabs wert={activeTab} onChange={setActiveTab} mobil={mobil} />

        {/* Tab Content */}
        <div className="animate-in fade-in duration-500">
          {activeTab === 'general' ? (
            <AllgemeinTab profil={userProfile} breit={breite >= 760} />
          ) : (
            <SicherheitTab breite={breite} mobil={mobil} newPassword={newPassword} confirmPassword={confirmPassword}
              showPasswords={showPasswords} loading={loading} onNeu={setNewPassword} onBestaetigt={setConfirmPassword}
              onSichtbar={() => setShowPasswords(!showPasswords)} onSubmit={(e) => void handlePasswordChange(e)} onHistorie={handleOpenHistory} />
          )}
        </div>
      </div>
      <GlasTooltipSchicht />

      {imageToCrop && (
        <BildAnpassenOverlay bild={imageToCrop} crop={crop} zoom={zoom} uploading={uploading} mobil={mobil}
          onCrop={setCrop} onZoom={setZoom} onCropComplete={onCropComplete}
          onAbbrechen={() => setImageToCrop(null)} onSpeichern={() => void handleUploadCroppedImage()} />
      )}

      {showHistory && (
        <LoginHistorieOverlay laedt={loadingHistory} eintraege={history} mobil={mobil} onSchliessen={() => setShowHistory(false)} />
      )}
    </>
  );
};

/* --- Hilfskomponenten --- */



export default ProfileView;
